package com.karyadina.cloudsync.service

import com.karyadina.cloudsync.model.*
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow

/**
 * Sync Engine
 * Core logic for orchestrating sync operations:
 * - Retrieve local data
 * - Encrypt data
 * - Upload to Google Drive
 * - Download from Google Drive
 * - Decrypt data
 * - Merge with conflict resolution (last-write-wins)
 */
interface SyncEngine {
    /**
     * Pending sync items (items waiting to be synced)
     */
    val pendingSyncItems: StateFlow<List<PendingSyncItem>>
    
    /**
     * Total pending size in MiB
     */
    val pendingSizeInMiB: StateFlow<Double>
    
    /**
     * Sync status
     */
    val syncStatus: StateFlow<SyncStatus>
    
    /**
     * Queue item for sync
     * Called when data is created/updated/deleted locally
     */
    suspend fun queueItem(
        itemId: String,
        itemType: String,
        size: Long,
        operation: SyncOperation
    ): Result<Unit>
    
    /**
     * Execute sync operation
     * Uploads pending items, downloads remote changes
     */
    suspend fun executeSync(): Result<SyncResult>
    
    /**
     * Clear pending queue (usually after successful sync)
     */
    suspend fun clearPendingQueue(): Result<Unit>
    
    /**
     * Get estimated sync time based on pending size and network speed
     */
    fun estimateSyncTime(): Long // in milliseconds
    
    /**
     * Set selective sync preferences
     */
    suspend fun setSelectiveSyncPreferences(prefs: SelectiveSyncPreferences): Result<Unit>
    
    /**
     * Get selective sync preferences
     */
    fun getSelectiveSyncPreferences(): SelectiveSyncPreferences
}

/**
 * Sync result with details
 */
data class SyncResult(
    val success: Boolean,
    val itemsSynced: Int,
    val failedItems: List<String> = emptyList(),
    val syncTime: Long, // milliseconds
    val errorMessage: String = ""
)
